package com.hyperether.goodjob.scenes.jobs.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.navigation.NavHostController
import com.hyperether.goodjob.log
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.navigation.JobDetails
import com.hyperether.goodjob.scenes.components.JobHeader
import com.hyperether.goodjob.scenes.components.clickableWithPointer
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.components.verticalScrollbar
import com.hyperether.goodjob.util.DateTimeUtil
import com.hyperether.goodjob.util.StatusColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import kotlinx.datetime.LocalDate

@Composable
fun JobListWeb(
    sites: Map<String, Site>?,
    jobsSorted: List<Map.Entry<Comparable<LocalDate>, List<Job>>>,
    navHostController: NavHostController,
    isAllSelected: Boolean,
    onAllSelectedChange: (Boolean) -> Unit,
    selectedJobs: List<Job>,
    onJobSelectedChange: (Job, Boolean) -> Unit
) {
    log.d("JobListWeb") { "recompose: jobsSorted size: ${jobsSorted.size}" }
    val scrollState = rememberScrollState()
    Column(
        modifier = Modifier.fillMaxSize()
            .verticalScrollbar(scrollState)
            .verticalScroll(scrollState)
            .disableMouseDragScrollOnWeb()
    ) {
        jobsSorted.forEach { (date, jobsForDate) ->
            val formattedDate = if (date is LocalDate) {
                DateTimeUtil.formatDateForDisplay(date)
            } else {
                date.toString()
            }
            if (jobsForDate.isNotEmpty()) {
                Spacer(modifier = Modifier.height(20.toScaleDp()))
                JobHeader(createdAt = formattedDate)
                DisableSelection {
                    jobsForDate.forEachIndexed { index, job ->
                        JobListRowWithCheckbox(
                            navHostController = navHostController,
                            job = job,
                            isEvenRow = index % 2 == 0,
                            location = sites?.get(job.contactSiteId)?.addressObject?.address ?: "",
                            isChecked = selectedJobs.contains(job),
                            onCheckedChange = { isChecked ->
                                onJobSelectedChange(
                                    job,
                                    isChecked
                                )
                            }
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(16.toScaleDp()))
        }
    }
}

@Composable
fun JobListRowWithCheckbox(
    navHostController: NavHostController,
    job: Job,
    isEvenRow: Boolean,
    location: String = "",
    isChecked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    val backgroundColor = if (isEvenRow) Color(0xFFF9F9F9) else Color.White
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .height(48.toScaleDp())
            .background(backgroundColor)
            .clickableWithPointer {
                job.id?.let { navHostController.navigate(JobDetails(it)) }
            }
            .pointerHoverIcon(PointerIcon.Hand)
            .padding(horizontal = 22.toScaleDp())
    ) {
        val jobTitle = job.jobTitle ?: " "
        Text(
            text = jobTitle,
            color = Color(0xFF34618E),
            fontSize = 14.toScaleSp(),
            fontWeight = FontWeight.Bold,
            modifier = Modifier.weight(0.9f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )

        val contactName = job.contactSiteName ?: " "
        Text(
            text = contactName,
            color = Color(0xFF323232),
            fontSize = 14.toScaleSp(),
            modifier = Modifier.weight(1.1f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        Text(
            text = location,
            color = Color(0xFF555555),
            fontSize = 14.toScaleSp(),
            modifier = Modifier.weight(1.6f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )

        val jobTime = job.start_at?.let { start ->
            job.end_at?.let { end ->
                "$start - $end"
            }
        } ?: ""
        Text(
            text = jobTime,
            color = Color(0xFF555555),
            fontSize = 14.toScaleSp(),
            modifier = Modifier.weight(0.6f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )

        val assignee = job.assignees?.employees?.mapNotNull {
            it?.getName()
        }?.joinToString(", ") ?: ""
        Text(
            text = assignee,
            color = Color(0xFF323232),
            fontSize = 14.toScaleSp(),
            modifier = Modifier.weight(0.5f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        Box(
            modifier = Modifier
                .weight(0.5f)
                .fillMaxWidth(),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .size(16.toScaleDp())
                    .clip(CircleShape)
                    .background(StatusColor.getStatusColor(job.status))
            )
        }
    }
}